import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../services/api";
import { Upload, FileText, Plus, X, Check, Briefcase, GraduationCap, Building2, ChevronLeft } from "lucide-react";

const OpportunitySeeker = ({ docUpload }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [isProfileFetched, setIsProfileFetched] = useState(
    localStorage.getItem("isProfileFetched") === "true"
  );
  
  // Custom document states
  const [customOpportunityDocs, setCustomOpportunityDocs] = useState({});
  const [showAddCustomOpportunity, setShowAddCustomOpportunity] = useState(false);
  const [newCustomOpportunityDocName, setNewCustomOpportunityDocName] = useState('');
  
  // Minimum character requirement for custom documents
  const MIN_CUSTOM_DOC_CHARS = 3;

  useEffect(() => {
    if (!isProfileFetched) {
      getProfile();
    } else {
      const savedUser = localStorage.getItem("currentUser");
      if (savedUser) {
        const user = JSON.parse(savedUser);
        setUserData(user);
      }
    }
  }, [isProfileFetched]);

  const getProfile = async () => {
    try {
      const response = await api.getProfile();
      setUserData(response.user_data);
      setIsProfileFetched(true);
      localStorage.setItem("isProfileFetched", "true");
      localStorage.setItem("currentUser", JSON.stringify(response.user_data));
    } catch (error) {
      console.error("Failed to fetch documents:", error);
    }
  };

  const getDropboxViewLink = (url) => {
    if (!url) return "";
    if (url.includes(".pdf")) {
      return url.replace("dl=0", "raw=1");
    }
    return url.replace("www.dropbox.com", "dl.dropboxusercontent.com");
  };

  const prettifyFieldName = (field) => {
    return field.replace(/_file_url$/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const handleFileUpload = async (fileFieldName, file) => {
    setUploading(true);
    setUploadingField(fileFieldName);

    try {
      console.log("Uploading file:", fileFieldName, file.name);

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error("File size must be less than 10MB");
      }

      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        throw new Error("Please upload only JPG, PNG, or PDF files");
      }

      if (!docUpload) {
        throw new Error("Document upload function not available");
      }

      // Determine if this is a custom opportunity doc
      let customDocCategories = {};
      if (fileFieldName.startsWith('custom_opportunity_')) {
        customDocCategories[fileFieldName] = 'opportunity_seeker';
      }

      // Send custom_doc_categories if needed
      await docUpload({ 
        [fileFieldName]: file, 
        ...(Object.keys(customDocCategories).length > 0 ? { custom_doc_categories: customDocCategories } : {}) 
      });

      // Wait a bit then refresh the profile data
      setTimeout(() => {
        getProfile();
        setUploading(false);
        setUploadingField(null);
      }, 1000);
    } catch (error) {
      console.error("Upload error:", error);
      setUploading(false);
      setUploadingField(null);

      // Show error message
      const messageElement = document.createElement("div");
      messageElement.textContent = `Upload failed: ${error.message}`;
      messageElement.style.cssText =
        "position: fixed; top: 70px; right: 45%; padding: 10px; background: #f56565; color: white; border-radius: 4px; z-index: 1000;";
      document.body.appendChild(messageElement);
      setTimeout(() => document.body.removeChild(messageElement), 5000);
    }
  };

  const handleDeleteDoc = async (field) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this document?"
    );
    if (!confirmDelete) return;

    try {
      const response = await api.deleteDocument(field);
      if (response.success) {
        await getProfile();

        // Show success message
        const messageElement = document.createElement("div");
        messageElement.textContent = "Document deleted successfully!";
        messageElement.style.cssText =
          "position: fixed; top: 70px; right: 45%; padding: 10px; background: #4CAF50; color: white; border-radius: 4px; z-index: 1000;";
        document.body.appendChild(messageElement);
        setTimeout(() => document.body.removeChild(messageElement), 3000);
      }
    } catch (err) {
      console.error("Failed to delete document:", err);
      alert("Something went wrong while deleting the document, try again...");
    }
  };

  const handleAddCustomOpportunityDoc = () => {
    if (newCustomOpportunityDocName.trim().length < MIN_CUSTOM_DOC_CHARS) return;
    
    const customDoc = {
      id: `custom_opportunity_${Date.now()}`,
      label: newCustomOpportunityDocName.trim(),
      icon: FileText,
      isCustom: true
    };
    
    setCustomOpportunityDocs(prev => ({
      ...prev,
      [customDoc.id]: customDoc
    }));
    
    setNewCustomOpportunityDocName('');
    setShowAddCustomOpportunity(false);
  };

  if (!userData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading documents...</p>
        </div>
      </div>
    );
  }

  const allDocuments = userData?.all_documents || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Opportunity Seeker</h1>
          <p className="text-sm text-gray-500 mt-1">Documents for Jobs, Internships & Admissions</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl shadow-blue-900/5 p-6 border border-white/60 backdrop-blur-sm">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-50 rounded-xl">
                <Briefcase className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Opportunity Seeker Documents</h3>
                <p className="text-[10px] text-gray-500 font-medium">
                  Required for Jobs, Internships & Admissions
                </p>
              </div>
            </div>
          </div>

          {/* Document Upload Fields - List Style like Document Vault */}
          <div className="space-y-3 min-h-[300px]">
            {/* Predefined Documents */}
            {[
              { id: 'resume_cv', label: 'Resume / CV', icon: FileText, required: true },
              { id: 'cover_letter', label: 'Cover Letter', icon: FileText, required: false },
              { id: 'experience_certificate', label: 'Experience Certificate', icon: Briefcase, required: false },
              { id: 'internship_certificate', label: 'Internship Certificate', icon: Building2, required: false },
              { id: 'project_portfolio', label: 'Project Portfolio', icon: GraduationCap, required: false }
            ].map((doc) => {
              const fieldKey = doc.id + '_file_url';
              const url = allDocuments[fieldKey];
              const isUploaded = !!url;
              const DocIcon = doc.icon;
              const isUploadingThis = uploadingField === fieldKey;

              return (
                <div
                  key={doc.id}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isUploaded
                          ? 'border-green-200 bg-green-50'
                          : 'border-gray-100 hover:border-gray-200 bg-gray-50'
                      }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                      {isUploadingThis ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-300 border-t-transparent"></div>
                      ) : isUploaded ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <DocIcon className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {doc.label}
                        {doc.required && <span className="text-red-500 ml-1">*</span>}
                      </p>
                      {isUploaded && url && (
                        <p className="text-xs text-green-600">{prettifyFieldName(fieldKey)}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isUploaded && url ? (
                      <>
                        <a
                          href={getDropboxViewLink(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200"
                        >
                          View
                        </a>
                        <button
                          onClick={() => handleDeleteDoc(fieldKey)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <label className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer ${isUploadingThis ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {isUploadingThis ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-300 border-t-transparent"></div>
                            <span>Uploading...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload
                          </>
                        )}
                        <input
                          type="file"
                          accept=".pdf,image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file && !isUploadingThis) handleFileUpload(fieldKey, file);
                          }}
                          disabled={isUploadingThis || uploading}
                        />
                      </label>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Custom Documents */}
            {Object.keys(customOpportunityDocs).map((docId) => {
              const doc = customOpportunityDocs[docId];
              const fieldKey = doc.id + '_file_url';
              const url = allDocuments[fieldKey];
              const isUploaded = !!url;
              const isUploadingThis = uploadingField === fieldKey;

              return (
                <div
                  key={docId}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all ${isUploaded
                          ? 'border-green-200 bg-green-50'
                          : 'border-dashed border-gray-200 bg-gray-50'
                      }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${isUploaded ? 'bg-green-100' : 'bg-white'} flex items-center justify-center`}>
                      {isUploadingThis ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-300 border-t-transparent"></div>
                      ) : isUploaded ? (
                        <Check className="w-5 h-5 text-green-500" />
                      ) : (
                        <FileText className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{doc.label}</p>
                      <span className="text-[10px] text-gray-400 bg-gray-200 px-2 py-0.5 rounded-full">Custom</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isUploaded && url ? (
                      <>
                        <a
                          href={getDropboxViewLink(url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200"
                        >
                          View
                        </a>
                        <button
                          onClick={() => handleDeleteDoc(fieldKey)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <label className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer ${isUploadingThis ? 'opacity-50 cursor-not-allowed' : ''}`}>
                          {isUploadingThis ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-300 border-t-transparent"></div>
                              <span>Uploading...</span>
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Upload
                            </>
                          )}
                          <input
                            type="file"
                            accept=".pdf,image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file && !isUploadingThis) handleFileUpload(fieldKey, file);
                            }}
                            disabled={isUploadingThis || uploading}
                          />
                        </label>
                        <button
                          onClick={() => {
                            setCustomOpportunityDocs(prev => {
                              const updated = { ...prev };
                              delete updated[docId];
                              return updated;
                            });
                            if (url) handleDeleteDoc(fieldKey);
                          }}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Add Custom Document */}
            {showAddCustomOpportunity ? (
              <div className="flex items-center gap-3 p-4 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50">
                <input
                  type="text"
                  value={newCustomOpportunityDocName}
                  onChange={(e) => setNewCustomOpportunityDocName(e.target.value)}
                  placeholder={`Enter document name (min ${MIN_CUSTOM_DOC_CHARS} characters)`}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCustomOpportunityDocName.trim().length >= MIN_CUSTOM_DOC_CHARS) {
                      handleAddCustomOpportunityDoc();
                    }
                    if (e.key === 'Escape') {
                      setShowAddCustomOpportunity(false);
                      setNewCustomOpportunityDocName('');
                    }
                  }}
                />
                <button
                  onClick={handleAddCustomOpportunityDoc}
                  disabled={newCustomOpportunityDocName.trim().length < MIN_CUSTOM_DOC_CHARS}
                  className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setShowAddCustomOpportunity(false);
                    setNewCustomOpportunityDocName('');
                  }}
                  className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAddCustomOpportunity(true)}
                className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-gray-200 text-gray-500 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50/50 transition-all"
              >
                <Plus className="w-5 h-5" />
                <span className="font-medium">Add Custom Document</span>
              </button>
            )}
          </div>
        </div>

        {/* Security Footer */}
        <p className="mt-4 text-center text-[10px] text-gray-400 font-medium">
          Your data is secure and encrypted. Need help? <span className="text-purple-500 cursor-pointer hover:underline">Contact Support</span>
        </p>
      </div>
    </div>
  );
};

export default OpportunitySeeker;

